import { useServerUploadDocument } from '@/hooks/useVault';
import {
  X,
  Upload,
  File,
  Calendar,
  FileText,
  Loader2,
} from 'lucide-react';
import { useState, useRef, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';

const DOCUMENT_TYPES = [
  { value: 'payslip', label: 'Bulletin de paie' },
  { value: 'contract', label: 'Contrat de travail' },
  { value: 'certificate', label: 'Attestation' },
  { value: 'edition', label: 'Edition' },
  { value: 'other', label: 'Autre' },
];

const buildLocalizedMonths = (locale: string) => {
  return Array.from({ length: 12 }, (_, idx) => {
    const monthNumber = idx + 1;
    const date = new Date(2000, idx, 1);
    const label = date.toLocaleString(locale || 'fr-FR', { month: 'long' });
    const capitalized = label.charAt(0).toUpperCase() + label.slice(1);
    return { value: String(monthNumber), label: capitalized };
  });
};

const YEARS = Array.from({ length: 3 }, (_, i) => {
  const year = new Date().getFullYear() - i;
  return { value: year.toString(), label: year.toString() };
});

interface VaultUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  vaultId: string;
  vaultName: string;
}

export function VaultUploadModal({
  isOpen,
  onClose,
  vaultId,
  vaultName,
}: VaultUploadModalProps) {
  const { t, i18n } = useTranslation();
  const MONTHS = useMemo(
    () => buildLocalizedMonths(i18n.language),
    [i18n.language]
  );

  const [file, setFile] = useState<File | null>(null);
  const [docType, setDocType] = useState('payslip');
  const [month, setMonth] = useState((new Date().getMonth() + 1).toString());
  const [year, setYear] = useState(new Date().getFullYear().toString());
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { mutate: uploadDocument, isPending: isUploading } =
    useServerUploadDocument(vaultId);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      setFile(files[0]);
    }
  }, []);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setFile(e.target.files[0]);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const resetForm = () => {
    setFile(null);
    setDocType('payslip');
    setMonth((new Date().getMonth() + 1).toString());
    setYear(new Date().getFullYear().toString());
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    if (!isUploading) {
      resetForm();
      onClose();
    }
  };

  const handleUpload = () => {
    if (!file) return;

    uploadDocument(
      {
        file,
        documentType: docType,
        year: parseInt(year),
        month: parseInt(month),
      },
      {
        onSuccess: () => {
          resetForm();
          onClose();
        },
      }
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-neutral-900/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      {/* Modal */}
      <div className="relative mx-4 w-full max-w-lg rounded-2xl bg-white shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
              <Upload className="h-5 w-5 text-purple-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-900">
                {t('vault.upload.title', 'Deposer un document')}
              </h3>
              <p className="text-sm text-neutral-500">{vaultName}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isUploading}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-100 hover:text-neutral-600 disabled:opacity-50"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Body */}
        <div className="space-y-5 px-6 py-5">
          {/* Document Type */}
          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-700">
              <FileText className="mr-1.5 inline h-4 w-4" />
              {t('vault.upload.documentType', 'Type de document')}
            </label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-neutral-900 focus:border-transparent focus:ring-2 focus:ring-purple-500"
            >
              {DOCUMENT_TYPES.map((dt) => (
                <option key={dt.value} value={dt.value}>
                  {t(`vault.documentType.${dt.value}`, dt.label)}
                </option>
              ))}
            </select>
          </div>

          {/* Period */}
          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-700">
              <Calendar className="mr-1.5 inline h-4 w-4" />
              {t('vault.upload.period', 'Periode')}
            </label>
            <div className="grid grid-cols-2 gap-3">
              <select
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-neutral-900 focus:border-transparent focus:ring-2 focus:ring-purple-500"
              >
                {MONTHS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              <select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="rounded-lg border border-neutral-300 bg-white px-3 py-2 text-neutral-900 focus:border-transparent focus:ring-2 focus:ring-purple-500"
              >
                {YEARS.map((y) => (
                  <option key={y.value} value={y.value}>
                    {y.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* File Drop Zone */}
          <div>
            <label className="mb-2 block text-sm font-medium text-neutral-700">
              {t('vault.upload.file', 'Fichier')}
            </label>
            <input
              ref={fileInputRef}
              type="file"
              onChange={handleFileSelect}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
              className="hidden"
            />

            {!file ? (
              <div
                onDragEnter={handleDragEnter}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed p-6 transition-colors ${
                  isDragging
                    ? 'border-purple-500 bg-purple-50'
                    : 'border-neutral-300 hover:border-purple-400 hover:bg-neutral-50'
                }`}
              >
                <Upload
                  className={`h-8 w-8 ${isDragging ? 'text-purple-500' : 'text-neutral-400'}`}
                />
                <p className="mt-2 text-sm font-medium text-neutral-700">
                  {t('vault.upload.dropZone', 'Glissez-deposez un fichier ici')}
                </p>
                <p className="mt-1 text-xs text-neutral-500">
                  {t('vault.upload.dropZoneHint', 'ou cliquez pour parcourir')}
                </p>
                <p className="mt-2 text-xs text-neutral-400">
                  PDF, DOC, DOCX, XLS, XLSX, JPG, PNG (max 50 MB)
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-3 rounded-lg border border-neutral-200 bg-neutral-50 p-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-purple-100">
                  <File className="h-5 w-5 text-purple-600" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-neutral-900">
                    {file.name}
                  </p>
                  <p className="text-xs text-neutral-500">
                    {formatFileSize(file.size)}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setFile(null);
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                    }
                  }}
                  disabled={isUploading}
                  className="flex h-8 w-8 items-center justify-center rounded-lg text-neutral-400 transition-colors hover:bg-neutral-200 hover:text-neutral-600 disabled:opacity-50"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 border-t border-neutral-200 px-6 py-4">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={isUploading}
          >
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button
            onClick={handleUpload}
            disabled={isUploading || !file}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {isUploading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('vault.upload.uploading', 'Depot en cours...')}
              </>
            ) : (
              <>
                <Upload className="mr-2 h-4 w-4" />
                {t('vault.upload.submit', 'Deposer')}
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
